/**
 * Overlay a logged-in viewer's identity + attendee attributes onto a form's
 * initial data.
 *
 * Forms are user-built with arbitrary, author-written field labels, so we can't
 * rely on stable field IDs — and we can't rely on exact label text either (a
 * t-shirt field might be "T-shirt size", "Shirt size", "tshirt size", or
 * "What's your tee size?"). We normalize the label (lowercase, strip
 * punctuation/spaces) and match with an ordered list of rules, most-specific
 * first; the first matching rule wins. Identity name rules stay strict so a
 * field like "Company name" isn't hijacked by the viewer's name; the richer
 * attendee attributes use tolerant substring/token matching.
 *
 * For choice fields (dropdown/multiple-choice/multi-select) the candidate value
 * is only written when it matches one of the field's choices (case-insensitive),
 * and the canonical choice string is used — so "S" selects the "S" option and an
 * unmatched value is left blank rather than writing an invalid option.
 */

import { isAnswerableField } from "@/features/forms/_logic/field-types";
import type { FormField } from "@/lib/database/forms";
import { nationalDigitsForCountry } from "@/lib/phone/phone-utils";
import type { ViewerIdentity } from "../actions/_shared/get-invited-page-access";
import { FIRST_NAME_KEYS, FULL_NAME_KEYS, LAST_NAME_KEYS, normalizeLabel } from "./label-matching";

/** True when the normalized key contains any of the given tokens. */
function hasAny(key: string, tokens: string[]): boolean {
  return tokens.some((token) => key.includes(token));
}

/** True when the normalized key contains all of the given tokens. */
function hasAll(key: string, tokens: string[]): boolean {
  return tokens.every((token) => key.includes(token));
}

// Identity name matching stays strict (exact normalized match, keys in ./label-matching) so
// generic "name"-bearing labels ("company name", "event name", "badge name") aren't filled with
// the viewer's own name.

// Email matching stays strict (exact normalized match) so qualified-email labels
// ("manager's email", "company email", "emergency contact email") aren't filled
// with the viewer's own address.
const EMAIL_KEYS = new Set(["email", "emailaddress", "youremail"]);

const CHOICE_FIELD_TYPES = new Set(["dropdown", "multiplechoice", "multiselect"]);

/**
 * Resolve a candidate string into a value appropriate for the field, honoring
 * choice fields. Returns `{ ok: false }` when there's nothing to write (empty
 * candidate, or no matching choice on a choice field).
 */
function resolveValueForField(
  field: FormField,
  candidate: string | null | undefined
): { ok: true; value: string | string[] } | { ok: false } {
  const trimmed = candidate?.trim();
  if (!trimmed) return { ok: false };

  const choices = field.choices;
  const isChoiceField = CHOICE_FIELD_TYPES.has(field.type) && Array.isArray(choices) && choices.length > 0;

  if (isChoiceField) {
    const match = choices!.find((choice) => choice.trim().toLowerCase() === trimmed.toLowerCase());
    if (!match) return { ok: false };
    return { ok: true, value: field.type === "multiselect" ? [match] : match };
  }

  return { ok: true, value: trimmed };
}

/**
 * Pick the attendee/identity candidate string for a field based on its
 * normalized label. Returns null when no rule matches (phone is handled
 * separately because its value is an object, not a string).
 */
function pickCandidate(key: string, viewer: ViewerIdentity, fullName: string): string | null {
  if (EMAIL_KEYS.has(key)) return viewer.email;

  // T-shirt before any generic name/size handling.
  if (hasAny(key, ["tshirt", "teeshirt", "shirtsize"]) || hasAll(key, ["shirt", "size"])) return viewer.tshirtSize;

  if (hasAny(key, ["dietary", "allerg"]) || hasAll(key, ["food", "restriction"]) || hasAll(key, ["food", "preference"]))
    return viewer.dietaryRestrictions;

  if (hasAny(key, ["accessibility", "accessible", "mobilityneed"])) return viewer.accessibilityNeeds;

  if (hasAny(key, ["ramadan"])) return viewer.ramadanAccommodations;

  if (hasAny(key, ["plusone"]) || hasAll(key, ["guest", "name"])) return viewer.namePlusOne;

  // Strict identity name matching (exact normalized match).
  if (FIRST_NAME_KEYS.has(key)) return viewer.firstName;
  if (LAST_NAME_KEYS.has(key)) return viewer.lastName;
  if (FULL_NAME_KEYS.has(key)) return fullName;

  return null;
}

const PHONE_KEYS = ["phone", "mobile", "cell"];

/**
 * Returns a new form-data map with identity + attendee fields overlaid from the
 * viewer.
 *
 * Skips fields whose initial value is already non-empty (so a user revisiting a
 * partially-filled form, or a restored saved response, keeps their input).
 *
 * @param formData - Initial form data keyed by field label
 * @param fields - The form's field definitions
 * @param viewer - Logged-in viewer identity, or null to no-op
 * @returns A new formData map with matchable fields populated
 */
export function prefillFormDataFromViewer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form data values are heterogeneous
  formData: Record<string, any>,
  fields: FormField[],
  viewer: ViewerIdentity | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  if (!viewer) return formData;

  const next = { ...formData };
  const fullName = `${viewer.firstName} ${viewer.lastName}`.trim();

  for (const field of fields) {
    if (!isAnswerableField(field)) continue;

    const existing = next[field.label];
    if (existing && typeof existing === "string" && existing.trim() !== "") continue;
    if (existing && typeof existing === "object" && "phone" in existing && existing.phone) continue;
    if (Array.isArray(existing) && existing.length > 0) continue;

    const key = normalizeLabel(field.label);

    // Phone is special-cased. Only a `tel` field renders the phone widget that
    // understands the object value — so only there do we write the object. The
    // stored phoneNumber may carry the country dial code baked in (e.g. UK
    // "447534162808" for +44); strip it to the national number so the field
    // doesn't render the dial code twice alongside the dial-code selector.
    //
    // A phone-LABELED field that's stored as plain `text` (the common case)
    // renders a plain text input — handing it an object leaves it blank, so we
    // write the stored number as a string instead and let it display as-is.
    if (hasAny(key, PHONE_KEYS)) {
      if (viewer.phoneNumber) {
        if (field.type === "tel") {
          const national = nationalDigitsForCountry(viewer.phoneNumber, viewer.countryDialCodeId);
          next[field.label] = { phone: national, countryDialCodeId: viewer.countryDialCodeId };
        } else {
          next[field.label] = viewer.phoneNumber;
        }
      }
      continue;
    }

    const candidate = pickCandidate(key, viewer, fullName);
    if (candidate === null) continue;

    const resolved = resolveValueForField(field, candidate);
    if (resolved.ok) next[field.label] = resolved.value;
  }

  return next;
}
